// ─── Shared editor-surface routing (per-format editor surface) ───────────────
//
// The SINGLE source of truth for "which filename extension opens which editor
// surface, at which URL." A closed ext → served-route map (→ A.5) decides BOTH
// whether an "Open in editor" affordance appears AND where it opens: PDF → the
// WASM /editor; PPTX/DOCX → the editable O365 accessibility editor at /o365-view
// (Epic editor-support-o365-shared-edit-260714, Phase 6a — alt text, mark
// decorative, title, language edits save via the per-format editor-edit
// endpoint). XLSX opens the same O365 accessibility editor at
// /o365-view?format=xlsx (Epic xlsx-a11y-editor-260813 Phase 3 — the paged
// sheet-switcher view; read-only in Phase 3, write-back is Phase 4).
//
// WHY shared: this contract had two consumers — the SPA job cards
// (spa/renderers.ts) and the /examples gallery cards (examples.ts) — so it is
// extracted here on the second site (rule #11 / A.6). Both trees import the
// primitive core; the map is defined once, so a route fact (a new format, a
// surface rewire) is applied in exactly one place and BOTH pages light up.
//
// Cross-tree build wiring (§G-2): this file lives at web/static/shared/ so both
// the isolated `spa` tsconfig tree and the `examples` tsconfig tree can `include`
// it. It emits as ESM via web/static/tsconfig.esm-modules.emit.json (mirroring
// upload-page-count.ts, the existing shared-tier cross-tree module), served as
// /static/shared/editor-routing.js.
const _EDITOR_ROUTE_BY_EXT = Object.freeze({
    '.pdf': { surface: 'wasm-pdf', hrefFor: (id) => `/editor?jobId=${encodeURIComponent(id)}` },
    '.pptx': { surface: 'a11y-view-office', hrefFor: (id) => `/o365-view?jobId=${encodeURIComponent(id)}&format=pptx` },
    '.docx': { surface: 'a11y-view-office', hrefFor: (id) => `/o365-view?jobId=${encodeURIComponent(id)}&format=docx` },
    '.xlsx': { surface: 'a11y-view-office', hrefFor: (id) => `/o365-view?jobId=${encodeURIComponent(id)}&format=xlsx` },
    // .odp opens the SAME shared a11y editor at /o365-view?format=odp (the ODF
    // Impress analogue; libreoffice-odf Phase 5a). Route name is cosmetic — the
    // mount is shared (a cosmetic /odf-view alias is deferred, phase-5b review §5.2).
    '.odp': { surface: 'a11y-view-office', hrefFor: (id) => `/o365-view?jobId=${encodeURIComponent(id)}&format=odp` },
});
/** The route entry for a filename extension (including the leading dot), or null. */
export function editorRouteForExt(ext) {
    const key = ext.toLowerCase();
    return (key in _EDITOR_ROUTE_BY_EXT) ? _EDITOR_ROUTE_BY_EXT[key] : null;
}
/** True when the extension has an editor surface (PDF editor or O365 accessibility editor). */
export function supportsEditorExt(ext) {
    return editorRouteForExt(ext) !== null;
}
/**
 * The editor-surface URL for a handle of the given extension, or null when the
 * format has no surface. PDF opens the WASM editor; PPTX/DOCX open the editable
 * O365 accessibility editor. `handleId` is the editor's opaque handle — a bare
 * jobId for a backend job, or an `examples:<id>` example handle for a gallery artifact.
 */
export function editorHrefForExt(ext, handleId) {
    const route = editorRouteForExt(ext);
    return route === null ? null : route.hrefFor(handleId);
}
/** The lowercase extension of a filename (including the dot), or null. */
export function extOfFilename(filename) {
    const name = (filename ?? '').toLowerCase();
    const dot = name.lastIndexOf('.');
    if (dot < 0)
        return null;
    return name.slice(dot);
}
